// Sub Components tab of the Components item record — SLOTS you install into and remove from
// (H100's link list reworked 2026-10-07 per Aaron's R1: "kits is stock - sub components for
// components similar logic - different outcome. buy and sell kits install and remove sub
// components").
//
// Legacy-kits-LIKE (mod_parts/includes/tabs/parts_tab_kits.inc, renamed Sub Components at a
// maintenance status, :17): the slots are the PART's BOM (each child part × quantity,
// parts_ajax_handler.inc:147-205), a filled slot reads "In BOM", "Fill Slots" fills an empty one
// (js/parts_bom.js:14-56), a child the BOM does not name is listed with In BOM "No"; columns
// Part Number | Description, In BOM, Item Number, Serial Number, Status (:446-466).
// The OUTCOME is the component lifecycle (Aeronet.app app/items/sub_components.py):
//   Fill Slot / (+) Install  → POST /v6/items/{id}/sub-components/install — and onto the
//                              assembly's aircraft when it is fitted
//   Remove Sub Component     → POST /v6/items/{id}/sub-components/remove — the aircraft removal
//                              when the child is fitted, else an unlink with a reason
// The parent line is items.parent_id (GET /v6/items/{id}/sub-components). Ticks + ACTIONS, no
// row pencil (the detail-tab rule); a filled row's click opens that component's record.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DataGridPremium, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import { axios } from "../../../api";
import { CreateFab2 } from "../../../components/Buttons";
import { ContentGridBox } from "../../../components/DatagridProContnet";
import TenantDatePicker from "../../../components/TenantDatePicker";
import type { ActionMenuItem } from "../../../components/datagrid/ActionMenu";
import { useLocationOptions } from "../../../hooks/useLocationOptions";
import { commonDataGridPremiumSx } from "../../../utils/dataGridSx";
import { installable, todayIso } from "../../../aircraft/componentLifecycle";
import { ComponentSearchBy, linkSearchParams } from "../componentSearch";
import SubgridActions from "./SubgridActions";
import CustomFilterPanelPremium from "../../../components/CustomFilterPanelPremium";

// kept for the existing import path
export { linkSearchParams } from "../componentSearch";

export interface SubComponentRow {
  id: number;
  part_number: string | null;
  part_description: string | null;
  serial_number: string | null;
  tbo: string | null;
  status: number | null;
}

interface SubComponentResponse {
  parent: SubComponentRow | null;
  children: SubComponentRow[];
}

export interface SlotsResponse {
  item_id: number;
  fitted_aircraft_id: number | null;
  slots: Array<{
    bom_id: number;
    part_id: number;
    part_number: string | null;
    part_description: string | null;
    quantity: number;
    filled: SubComponentRow[];
    empty: number;
  }>;
  not_in_bom: SubComponentRow[];
}

export interface SlotGridRow {
  id: number | string;
  kind: "filled" | "empty";
  in_bom: "Yes" | "No";
  part_number: string | null;
  part_description: string | null;
  item_number: number | null;
  serial_number: string | null;
  status: string;
  /** For an empty slot: the part that fills it. */
  part_id?: number;
}

const STATUS: Record<number, string> = { 4: "Stock", 7: "Flying", 16: "Removed", 17: "Rotable", 18: "Retired" };

/** The grid's rows: each BOM slot's filled children then its empty places, then the children
 * the BOM does not name. */
export function slotGridRows(data: SlotsResponse | null): SlotGridRow[] {
  if (!data) return [];
  const rows: SlotGridRow[] = [];
  const filled = (c: SubComponentRow, inBom: "Yes" | "No"): SlotGridRow => ({
    id: c.id,
    kind: "filled",
    in_bom: inBom,
    part_number: c.part_number,
    part_description: c.part_description,
    item_number: c.id,
    serial_number: c.serial_number,
    status: STATUS[Number(c.status)] ?? String(c.status ?? ""),
  });
  for (const slot of data.slots) {
    slot.filled.forEach((c) => rows.push(filled(c, "Yes")));
    for (let k = 0; k < slot.empty; k++) {
      rows.push({
        id: `empty-${slot.bom_id}-${k}`,
        kind: "empty",
        in_bom: "Yes",
        part_number: slot.part_number,
        part_description: slot.part_description,
        item_number: null,
        serial_number: null,
        status: "Empty slot",
        part_id: slot.part_id,
      });
    }
  }
  data.not_in_bom.forEach((c) => rows.push(filled(c, "No")));
  return rows;
}

interface Props {
  itemId: number;
  locked: boolean;
  from: string;
}

const ComponentSubComponents: React.FC<Props> = ({ itemId, locked, from }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const locationOptions = useLocationOptions();
  const [parent, setParent] = useState<SubComponentRow | null>(null);
  const [slots, setSlots] = useState<SlotsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  // install: into an empty slot (its part prefilled) or anywhere (+)
  const [installFor, setInstallFor] = useState<{ open: boolean; partNumber: string }>({ open: false, partNumber: "" });
  const [searchBy, setSearchBy] = useState<ComponentSearchBy>("number");
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [dov, setDov] = useState(todayIso());
  // remove
  const [removing, setRemoving] = useState<SlotGridRow | null>(null);
  const [reason, setReason] = useState("");
  const [location, setLocation] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        axios.get(`/v6/items/${itemId}/sub-components`, { cache: false }),
        axios.get(`/v6/items/${itemId}/sub-components/slots`, { cache: false }),
      ]);
      setParent((p.data as SubComponentResponse)?.parent ?? null);
      setSlots((s.data as SlotsResponse) ?? null);
    } catch {
      enqueueSnackbar("Error fetching sub components", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => slotGridRows(slots), [slots]);
  const selected = rows.filter((r) => selection.includes(r.id));
  const fitted = Boolean(slots?.fitted_aircraft_id);

  const openItem = useCallback(
    (id: number) => navigate(`/components/items/${id}?from=${from}`),
    [from, navigate]
  );

  const startInstall = (partNumber: string) => {
    setInstallFor({ open: true, partNumber });
    setSearchBy("number");
    setSearchText(partNumber);
    setResults([]);
    setDov(todayIso());
  };

  const search = useCallback(async () => {
    if (!searchText.trim()) return;
    try {
      const resp = await axios.get("/v6/items", { params: linkSearchParams(searchBy, searchText), cache: false });
      const items = (resp.data?.items ?? []).filter(
        (i: any) => i.id !== itemId && installable(i) && !rows.some((r) => r.id === i.id)
      );
      setResults(items);
    } catch {
      enqueueSnackbar("Error searching components", { variant: "error" });
    }
  }, [enqueueSnackbar, itemId, rows, searchBy, searchText]);

  const install = async (childId: number) => {
    try {
      const resp = await axios.post(
        `/v6/items/${itemId}/sub-components/install`,
        { child_item_id: childId, date_of_values: dov },
        { cache: false }
      );
      setSlots(resp.data as SlotsResponse);
      setInstallFor({ open: false, partNumber: "" });
      setSelection([]);
      enqueueSnackbar(fitted ? "Sub component installed, and fitted to the aircraft" : "Sub component installed", {
        variant: "success",
      });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || "The sub component was NOT installed", { variant: "error" });
    }
  };

  const remove = async () => {
    if (!removing) return;
    if (!reason.trim()) {
      enqueueSnackbar("A reason for removal is required", { variant: "warning" });
      return;
    }
    try {
      const resp = await axios.post(
        `/v6/items/${itemId}/sub-components/remove`,
        { child_item_id: removing.id, reason, date_of_values: dov, location: location || null },
        { cache: false }
      );
      setSlots(resp.data as SlotsResponse);
      setRemoving(null);
      setReason("");
      setSelection([]);
      enqueueSnackbar("Sub component removed", { variant: "success" });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || "The sub component was NOT removed", { variant: "error" });
    }
  };

  const actions: ActionMenuItem[] = useMemo(() => {
    const one = selected.length === 1 ? selected[0] : null;
    const list: ActionMenuItem[] = [
      {
        name: "Open Component",
        requiresSelection: true,
        preventMultiSelect: true,
        multiSelectErrorMessage: "Select a single sub component.",
        disabled: !one || one.kind !== "filled",
        onClick: () => one && openItem(Number(one.id)),
      },
    ];
    if (!locked) {
      list.push(
        {
          name: "Fill Slot",
          requiresSelection: true,
          preventMultiSelect: true,
          multiSelectErrorMessage: "Select a single empty slot.",
          disabled: !one || one.kind !== "empty",
          onClick: () => one && startInstall(one.part_number ?? ""),
        },
        {
          name: "Remove Sub Component",
          requiresSelection: true,
          preventMultiSelect: true,
          multiSelectErrorMessage: "Select a single sub component.",
          disabled: !one || one.kind !== "filled",
          onClick: () => {
            if (!one) return;
            setRemoving(one);
            setReason("");
            setDov(todayIso());
          },
        }
      );
    }
    return list;
  }, [locked, openItem, selected]);

  const columns: GridColDef<SlotGridRow>[] = useMemo(
    () => [
      {
        field: "part_number",
        headerName: "Part Number | Description",
        flex: 1.8,
        valueGetter: (_v, row) => [row.part_number, row.part_description].filter(Boolean).join(" | "),
      },
      { field: "in_bom", headerName: "In BOM", flex: 0.5 },
      { field: "item_number", headerName: "Item Number", flex: 0.6 },
      { field: "serial_number", headerName: "Serial Number", flex: 0.9 },
      { field: "status", headerName: "Status", flex: 0.7 },
    ],
    []
  );

  return (
    <ContentGridBox>
      <Typography variant="body2" sx={{ mb: 2 }} data-testid="sub-component-parent">
        {parent ? (
          <>
            Parent component:{" "}
            <Box
              component="a"
              href={`/app/components/items/${parent.id}?from=${from}`}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                openItem(parent.id);
              }}
            >
              {parent.part_number} {parent.part_description} ({parent.serial_number || "N/A"})
            </Box>
          </>
        ) : (
          "Not a sub component of another component."
        )}
      </Typography>
      <Grid container rowSpacing={3} sx={{ pb: 2 }}>
        <Grid item xs={12} sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          {!locked ? (
            <CreateFab2
              size="small"
              title="Install Sub Component"
              aria-label="Install Sub Component"
              onClick={() => startInstall("")}
            />
          ) : null}
          <SubgridActions popupId="sub-components-actions" actions={actions} selectionCount={selection.length} />
        </Grid>
      </Grid>
      <DataGridPremium
        slots={{ filterPanel: CustomFilterPanelPremium }}
        autoHeight
        density="compact"
        rows={rows}
        columns={columns}
        loading={loading}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        onRowClick={(params) => {
          const row = params.row as SlotGridRow;
          if (row.kind === "filled") openItem(Number(row.id));
        }}
        hideFooter={rows.length <= 15}
        sx={{ ...commonDataGridPremiumSx, borderLeft: "none", borderRight: "none", ".MuiDataGrid-cell": { cursor: "pointer" } }}
        aria-label="sub-components"
      />

      <Dialog open={installFor.open} onClose={() => setInstallFor({ open: false, partNumber: "" })} fullWidth maxWidth="sm">
        <DialogTitle>{installFor.partNumber ? `Fill slot: ${installFor.partNumber}` : "Install Sub Component"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={4}>
              <TextField select fullWidth label="Search by" value={searchBy} onChange={(e) => setSearchBy(e.target.value as ComponentSearchBy)}>
                <MenuItem value="number">Part # / Name</MenuItem>
                <MenuItem value="serial">Serial #</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") search();
                }}
                helperText="Enter to search — removed components not in an assembly"
              />
            </Grid>
            <Grid item xs={12}>
              <TenantDatePicker label="Date of Values" fullWidth value={dov} onChange={setDov} />
              {fitted ? (
                <Typography variant="caption" color="text.secondary">
                  The assembly is fitted: the sub component is installed on its aircraft too, at the aircraft's current readings.
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <List dense data-testid="sub-component-install-results">
                {results.map((r: any) => (
                  <ListItemButton key={r.id} onClick={() => install(r.id)}>
                    <ListItemText
                      primary={`${r.part?.number ?? ""} ${r.part?.description ?? ""}`.trim() || `Item #${r.id}`}
                      secondary={`S/N ${r.serial_number || "N/A"} · Item #${r.id}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstallFor({ open: false, partNumber: "" })}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={removing !== null} onClose={() => setRemoving(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Remove {removing?.part_number ?? "sub component"} {removing?.serial_number ? `S/N ${removing.serial_number}` : ""}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }} data-testid="sub-component-remove-form">
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                label="Reason for removal"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TenantDatePicker label="Date of Values" fullWidth value={dov} onChange={setDov} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                required={fitted}
                label="Removal location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                {(locationOptions ?? []).map((o: any) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {fitted ? (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  It is fitted to the assembly's aircraft: this is the aircraft removal — its life is recorded, its
                  inspection / AD lines freeze, and its slot stays on the aircraft, empty.
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button variant="contained" onClick={remove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </ContentGridBox>
  );
};

export default ComponentSubComponents;
